import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Fulfillment, ShippingRate } from '@mayksel/types';
import type { CreateFulfillmentInput, UpdateFulfillmentInput, GetRatesInput, PurchaseLabelInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useFulfillments(orderId: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Fulfillment[]>({
    queryKey: ['fulfillments', workspaceId, orderId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Fulfillment[]>(`/v1/orders/${orderId}/fulfillments`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!orderId,
  });
}

export function useCreateFulfillment(orderId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateFulfillmentInput) => {
      const token = await getToken();
      return api.post<Fulfillment>(`/v1/orders/${orderId}/fulfillments`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fulfillments', workspaceId, orderId] });
    },
  });
}

export function useUpdateFulfillment(orderId: string, fulfillmentId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateFulfillmentInput) => {
      const token = await getToken();
      return api.patch<Fulfillment>(
        `/v1/orders/${orderId}/fulfillments/${fulfillmentId}`,
        data,
        { token: token ?? undefined, workspaceId: workspaceId ?? undefined },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fulfillments', workspaceId, orderId] });
    },
  });
}

export function useDeleteFulfillment(orderId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fulfillmentId: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(
        `/v1/orders/${orderId}/fulfillments/${fulfillmentId}`,
        { token: token ?? undefined, workspaceId: workspaceId ?? undefined },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fulfillments', workspaceId, orderId] });
    },
  });
}

export function useGetRates(orderId: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useMutation({
    mutationFn: async (data: GetRatesInput) => {
      const token = await getToken();
      return api.post<ShippingRate[]>(
        `/v1/orders/${orderId}/fulfillments/rates`,
        data,
        { token: token ?? undefined, workspaceId: workspaceId ?? undefined },
      );
    },
  });
}

export function usePurchaseLabel(orderId: string, fulfillmentId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PurchaseLabelInput) => {
      const token = await getToken();
      return api.post<Fulfillment>(
        `/v1/orders/${orderId}/fulfillments/${fulfillmentId}/purchase-label`,
        data,
        { token: token ?? undefined, workspaceId: workspaceId ?? undefined },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fulfillments', workspaceId, orderId] });
    },
  });
}
